import { type AuditFlag, type FlagColor } from './flagTypes'
import type { HiringIssue } from './hiringIssueTypes'
import { FormStatus } from './formTypes'
import type { HiringProcess, ProcessStage } from './processTypes'

export type FrequencyValue =
  | 'never'
  | 'rarely'
  | 'sometimes'
  | 'often'
  | 'always'

export interface ChallengeDetails {
  [key: string]: boolean | undefined
}

export interface CandidateExperienceDetails extends ChallengeDetails {
  slowInitialContact?: boolean
  candidateWithdrawal?: boolean
  candidatesGhosted?: boolean
}

export interface CandidateSourcingDetails extends ChallengeDetails {
  notEnoughApplicants?: boolean
  lowResponseRate?: boolean
  hardToFindSpecialists?: boolean
}

export interface ScreeningEvaluationDetails extends ChallengeDetails {
  screeningMismatch?: boolean
  inconsistentFeedback?: boolean
  assessmentStandardization?: boolean
}

export interface HiringProcessDetails extends ChallengeDetails {
  schedulingDelays?: boolean
  technicalRolesSlower?: boolean
  processRestarts?: boolean
}

export interface MatchingRolesDetails extends ChallengeDetails {
  technicalExpectationsMismatch?: boolean
  unclearJobDescriptions?: boolean
  poorCultureFit?: boolean
}

export interface InternalChallengesDetails extends ChallengeDetails {
  budgetConstraints?: boolean
  hiringManagerDelays?: boolean
  approvalProcessDelays?: boolean
}

export interface PostHireChallengesDetails extends ChallengeDetails {
  earlyAttrition?: boolean
  longerRampUpTime?: boolean
  missedPerformanceIssues?: boolean
}

export interface ChallengesData {
  candidateExperienceFrequency?: FrequencyValue
  candidateExperienceDetails?: CandidateExperienceDetails
  candidateSourcingFrequency?: FrequencyValue
  candidateSourcingDetails?: CandidateSourcingDetails
  screeningEvaluationFrequency?: FrequencyValue
  screeningEvaluationDetails?: ScreeningEvaluationDetails
  hiringProcessFrequency?: FrequencyValue
  hiringProcessDetails?: HiringProcessDetails
  matchingRolesFrequency?: FrequencyValue
  matchingRolesDetails?: MatchingRolesDetails
  internalChallengesFrequency?: FrequencyValue
  internalChallengesDetails?: InternalChallengesDetails
  postHireChallengesFrequency?: FrequencyValue
  postHireChallengesDetails?: PostHireChallengesDetails
}

enum HiringGoal {
  Fast = 'fast',
  Cheap = 'cheap',
  BestFit = 'bestFit',
}
enum SalaryTarget {
  BelowMarket = 'belowMarket',
  AtMarket = 'atMarket',
  AboveMarket = 'aboveMarket',
}

interface LinkedInProfile {
  [key: string]: string
}

export interface Process {
  hiringGoal: HiringGoal
  role: string
  seniority: string
  isNewPosition: boolean
  location: string
  isDegreeRequired: boolean
  minimumExperience: string
  essentialDuties: string
  techStack: string
  candidateOutreachGuidance: string
  isCurrentEmployeeEligible: boolean
  salaryBandLow: number
  salaryBandHigh: number
  currency: string
  salaryTarget: SalaryTarget
  additionalCompensation: string
  linkedInProfilesMap: Record<string, LinkedInProfile>
  isUsingAi: boolean
  aiUses: string
}

const hiringChallengeMap = {
  candidateExperience: 'candidateExperience',
  candidateSourcing: 'candidateSourcing',
  candidateScreeningAndEvaluation: 'candidateScreeningAndEvaluation',
  processEfficiency: 'processEfficiency',
  market: 'market',
  internal: 'internal',
  postHire: 'postHire',
  other: 'other',
} as const

export type HiringChallenge = keyof typeof hiringChallengeMap

export const hiringChallengeDisplayMap = {
  candidateExperience: 'Candidate Experience',
  candidateSourcing: 'Candidate Sourcing Challenges',
  candidateScreeningAndEvaluation: 'Candidate Screening and Evaluation',
  processEfficiency: 'Hiring Process Efficiency',
  market: 'Market Challenges',
  internal: 'Internal Challenges',
  postHire: 'Post-Hire Challenges',
  other: 'Other',
} as const

export type HiringChallengeDisplay =
  (typeof hiringChallengeDisplayMap)[HiringChallenge]

export const timeToFillPositionOptions = [
  '< 30 Days',
  '31 - 60 Days',
  '61 - 90 Days',
  '90+ Days',
] as const

type TimeToFillPosition = (typeof timeToFillPositionOptions)[number]

const costToHireOneTechnicalPosition = [
  '< $1000',
  '$1000 - $5000',
  '$5000 - $10000',
  '$10000+',
] as const

type CostToFillPosition = (typeof costToHireOneTechnicalPosition)[number]

export const numberOfTechnicalHires = [
  '1 - 10',
  '11 - 25',
  '26 - 50',
  '51 - 100',
  '101 - 500',
  '500+',
] as const

type NumberOfTechnicalHires = (typeof numberOfTechnicalHires)[number]

export const numberOfTechnicalRecruiters = [
  '0',
  '1 - 3',
  '4 - 6',
  '6 - 12',
  '12+',
] as const

type NumberOfTechnicalRecruiters = (typeof numberOfTechnicalRecruiters)[number]

export const numberOfEmployees = [
  '0 - 20',
  '21 - 50',
  '51 - 100',
  '101 - 500',
  '501 - 1000',
  '1000+',
] as const

type NumberOfEmployees = (typeof numberOfEmployees)[number]

export enum AuditType {
  Full = 'full',
  Quick = 'quick',
}

export interface NewAuditRequest {
  auditName: string
  auditType: AuditType
  auditData: object
}

interface AuditBaseFields {
  biggestChallenges?: HiringChallenge[]
  offerAcceptanceRate?: number
  isUsingExternalResources?: boolean
  timeToFillTechnicalPosition?: string
  closedRoleWithoutHiringWithinLast12Months?: boolean
  numberOfStages?: number
}

export interface QuickAuditData extends AuditBaseFields {}

export interface FullAuditData extends AuditBaseFields {
  issues?: Partial<Record<HiringIssue, boolean>>
  numberOfEmployeesRange?: string
  numberOfTechnicalEmployeesRange?: string
  numberOfTechnicalRecruitersRange?: string
  numberOfTechnicalRecruiters?: string | number
  numberOfTechnicalHiresLast12Months?: string | number
  numberOfTechnicalHiresNext12Months?: string | number
  costToHireOneTechnicalRole?: string
  longestOpenPositionLast12Months?: string
  salaryTarget?: string
  isUsingPsychTest?: boolean
  isRequiringReferences?: boolean
  processStages?: ProcessStage[]
  flags?: Record<FlagColor, AuditFlag>
  companyName?: string
  hasDedicatedSourcingSupport?: boolean
  usesAITools?: boolean
  engineerHoursPerHire?: string | number
  firstYearAttritionRate?: string
  externalHiresPercentage?: string
  usesStructuredInterviews?: boolean
  usesTechnicalAssessments?: boolean
  challenges?: ChallengesData
  offerAcceptanceRateRange?: string
  candidateExperienceFrequency?: FrequencyValue
  candidateSourcingFrequency?: FrequencyValue
  screeningEvaluationFrequency?: FrequencyValue
  hiringProcessFrequency?: FrequencyValue
  matchingRolesFrequency?: FrequencyValue
  internalChallengesFrequency?: FrequencyValue
  postHireChallengesFrequency?: FrequencyValue
}

export interface FullAudit {
  createdBy?: string
  createdByName?: string
  completedAt?: string
  id: string
  orgId: string
  status: FormStatus
  archived: boolean
  auditName: string
  auditType: string
  auditData: FullAuditData
  createdAt: string
  updatedAt: string
}

export type AuditBooleanField = keyof Pick<
  FullAuditData,
  | 'closedRoleWithoutHiringWithinLast12Months'
  | 'isRequiringReferences'
  | 'isUsingExternalResources'
  | 'isUsingPsychTest'
  | 'hasDedicatedSourcingSupport'
  | 'usesAITools'
  | 'usesStructuredInterviews'
  | 'usesTechnicalAssessments'
>

const auditTypeMap = {
  number: 'number',
  minMax: 'minMax',
} as const

interface NumberType {
  value: number
  type: typeof auditTypeMap.number
}

interface MinMaxType {
  value: [number, number]
  type: typeof auditTypeMap.minMax
}

export const fieldDefaultMap = {
  offerAcceptanceRate: { min: 71, max: 75 },
  costToHireOneTechnicalRole: { min: 0, max: 10000 },
}
